import { computed } from 'vue'
import type { AxiosError } from 'axios'

import { serverErrorMessage, type ApiErrorItem } from '@/shared/api'

import { isGloballyReported } from './mutationError'
import { scrollToFirstError } from './scrollToFirstError'

/** The slice of a TanStack mutation the submit pipeline drives. */
export type MutationLike<TPayload, TResponse> = {
  mutateAsync: (payload: TPayload) => Promise<TResponse>
  isPending: { value: boolean }
}

/** The slice of a `useEntityForm` result that the submit pipeline needs. */
export type ApiFormSource<TPayload> = {
  fields: string[]
  /** Codes the API uses for fields it does not name the same way — see `matchField`. */
  errorAliases?: Record<string, string>
  buildPayload: () => Promise<TPayload> | TPayload
  validateForm: () => boolean
  setServerErrors: (errors: Record<string, string>) => void
  clearServerErrors: () => void
}

const squash = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')

/**
 * The API answers with its own field names — snake_case, sometimes nested
 * (`traffic_rule.referrer`). Map each one onto the field the form actually has, so
 * `<FormItem :error="errors.creatureId">` needs no per-modal fallback. An unknown code is
 * kept verbatim rather than dropped: a visible error beats a silent one.
 */
function matchField(
  code: string,
  fields: string[],
  aliases: Record<string, string> = {}
) {
  if (aliases[code]) return aliases[code]

  if (!fields.length || fields.includes(code)) return code

  // A nested attribute, compared whole: `malware_scan_configuration_attributes.countries`
  // squashes to the same thing as the form's
  // `malwareScanConfigurationAttributes.countries`. It is tried before the tail below, or
  // the code would land on whichever unrelated field happens to share its last segment.
  const wholePath = fields.find((field) => squash(field) === squash(code))
  if (wholePath) return wholePath

  const tail = code.split('.').pop() ?? code
  if (fields.includes(tail)) return tail

  return fields.find((field) => squash(field) === squash(tail)) ?? code
}

/**
 * The key a message with no field of its own is filed under. `useEntityForm.formErrors`
 * collects exactly the server keys that match no field, so anything parked here renders
 * once above the form — the same place a `system` code from the API already lands.
 *
 * It is only used when the form has no field by that name, which `fields` decides.
 */
const FORM_LEVEL_KEY = 'system'

const formLevelKey = (fields: string[]) =>
  fields.includes(FORM_LEVEL_KEY) ? `${FORM_LEVEL_KEY}__form` : FORM_LEVEL_KEY

function parseApiErrors(
  error: unknown,
  fields: string[],
  aliases?: Record<string, string>
): Record<string, string> {
  const axiosError = error as AxiosError<{ errors?: ApiErrorItem[] }>
  const errors = axiosError.response?.data?.errors

  // Guarded rather than defaulted: `?? []` only covers a missing key, and the six shapes
  // an error body arrives in (see `serverErrorMessage`) include an `errors` that is a
  // string or a `{ field: [messages] }` map. `.reduce` on either throws — from inside
  // the catch block, which is the one place a throw has nowhere left to go.
  if (!Array.isArray(errors)) return {}

  return errors.reduce<Record<string, string>>((acc, item) => {
    const field = matchField(item.code, fields, aliases)

    acc[field] = fields.includes(field)
      ? (item.detail ?? item.title)
      : item.title
    return acc
  }, {})
}

/**
 * Submit pipeline for an entity form: validate → buildPayload → mutateAsync → map the API's
 * field errors back onto the fields. Bind the returned `submit` to `@submit` and
 * `isSubmitting` to the Save button, and write no error handling of your own.
 *
 * Pass the whole form under `entityForm` — the payload, the validation and both error sinks
 * are read off it. The separate callbacks are for the rare form that is not a
 * `useEntityForm`, such as a payload assembled from two sources. A form in a modal wants
 * `useModalForm`, which adds the close and the toast.
 *
 * **It never rejects.** A refused save leaves its messages on the fields and resolves to
 * `undefined`; a caller that needs to know reads the return, which is the response on
 * success and `undefined` whenever nothing was saved.
 */
export function useApiForm<TPayload, TResponse>(options: {
  mutation: MutationLike<TPayload, TResponse>

  entityForm?: ApiFormSource<TPayload>

  buildPayload?: () => Promise<TPayload> | TPayload
  validate?: () => boolean
  setServerErrors?: (errors: Record<string, string>) => void
  clearServerErrors?: () => void

  /**
   * Asked after the form validates and before anything is sent — a question only a valid
   * form can raise ("more than is available — pay out anyway?"). `false` sends nothing.
   */
  confirm?: () => boolean | Promise<boolean>

  onSuccess?: (response: TResponse) => void | Promise<void>
  onError?: (error: unknown) => void
}) {
  const buildPayload = options.buildPayload ?? options.entityForm?.buildPayload
  const validate = options.validate ?? options.entityForm?.validateForm
  const setServerErrors =
    options.setServerErrors ?? options.entityForm?.setServerErrors
  const clearServerErrors =
    options.clearServerErrors ?? options.entityForm?.clearServerErrors

  if (!buildPayload) {
    throw new Error('useApiForm: pass either `entityForm` or `buildPayload`')
  }

  // Re-bound after the guard: the narrowing above does not reach into `submit`,
  // which is a closure created later.
  const buildFormPayload = buildPayload

  const isSubmitting = computed(() => options.mutation.isPending.value)

  async function submit() {
    if (options.mutation.isPending.value) return

    // Before the validity guard, not after it, and not inside the `try`: the server's
    // complaint is about the previous attempt, and the field proxy's auto-clear reaches
    // only the field being edited — never the form-level key. Cleared any later, a locally
    // invalid attempt keeps last time's banner on screen beside this time's field errors.
    clearServerErrors?.()

    if (validate && !validate()) {
      void scrollToFirstError()
      return
    }

    if (options.confirm && !(await options.confirm())) return

    try {
      const payload = await buildFormPayload()
      const response = await options.mutation.mutateAsync(payload)

      await options.onSuccess?.(response)

      return response
    } catch (error) {
      const fields = options.entityForm?.fields ?? []

      const parsed = parseApiErrors(
        error,
        fields,
        options.entityForm?.errorAliases
      )

      // A rejection the field map could not read still has to reach the user. Nothing is
      // mapped whenever the body carried no `errors` array, which is every transport
      // failure: a dropped connection and the request timeout have no `response` at all,
      // and `head :bad_request` answers with an empty body. The interceptor speaks for 5xx
      // only, so without this Save spins, stops and says nothing — and the next press of it
      // writes a created record twice.
      if (!Object.keys(parsed).length && !isGloballyReported(error)) {
        parsed[formLevelKey(fields)] = serverErrorMessage(
          error,
          'Could not save. Please check your connection and try again.'
        )
      }

      setServerErrors?.(parsed)
      void scrollToFirstError()
      options.onError?.(error)

      // Handled, so deliberately not rethrown: the errors are on the fields, the first one
      // is scrolled to and `onError` has run. Rethrowing would send every refused save
      // through Vue's error handler into Sentry as an app crash, since an `@submit` binding
      // catches nothing. A caller that needs to know reads the return instead — `undefined`
      // here, as it is from the pending and invalid guards above.
      return undefined
    }
  }

  return {
    submit,
    isSubmitting
  }
}
